import React, { useState, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import {
  FaProjectDiagram,
  FaCheckCircle,
  FaTimesCircle,
  FaChartLine,
  FaRocket,
  FaSyncAlt,
  FaExclamationTriangle,
  FaTimes,
  FaFilter,
  FaStream,
  FaArrowDown,
  FaArrowRight
} from 'react-icons/fa';
import FlowSankeyChart from './FlowSankeyChart';
import { getFlowSankey } from '../../../../services/analytics.service';
import { normalizeAnalyticsError } from '../../shared/analyticsViewUtils';


/**
 * Step Funnel View provides a compact, high-clarity tabular conversion funnel
 * that maximizes screen real estate and minimizes vertical scrolling.
 */
function StepFunnelView({ nodes, summary }) {
  const steps = (nodes || []).filter((n) => (n.type || '').toLowerCase() === 'step');
  const totalLaunched = Number(summary?.totalLaunched || 1);
  const totalCompleted = Number(summary?.totalCompleted || 0);

  if (steps.length === 0) {
    return (
      <div className="sankey-empty-state">
        <FaFilter className="empty-state-icon" />
        <h4>No Funnel Steps Available</h4>
        <p>No step definitions exist for this flow journey.</p>
      </div>
    );
  }

  const overallRate = summary?.overallCompletionRate !== undefined && summary?.overallCompletionRate !== null
    ? `${summary.overallCompletionRate}%`
    : (totalLaunched > 0 ? `${((totalCompleted / totalLaunched) * 100).toFixed(1)}%` : '0%');

  return (
    <div className="step-funnel-container" data-testid="step-funnel-view">
      {/* Table Header */}
      <div className="funnel-table-header">
        <div className="col-step-name">Step</div>
        <div className="col-progress">Progression (% Reached)</div>
        <div className="col-dropoff">Step Drop-off</div>
        <div className="col-transition">Next Step Conversion</div>
      </div>

      {/* Table Body */}
      <div className="funnel-table-body">
        {steps.map((step, idx) => {
          const reached = Number(step.count ?? step.viewCount ?? step.value ?? 0);
          const dropped = Number(step.exitCount ?? 0);
          const continued = Math.max(0, reached - dropped);
          const reachedPct = Math.min(100, Math.max(0, (reached / totalLaunched) * 100));
          const droppedPct = reached > 0 ? ((dropped / reached) * 100).toFixed(1) : '0';
          const convRate = step.completionRate !== null && step.completionRate !== undefined
            ? `${step.completionRate}%`
            : (reached > 0 ? `${((continued / reached) * 100).toFixed(1)}%` : '0%');
          const isLastStep = idx === steps.length - 1;

          return (
            <div key={step.id || `step-${idx}`} className="funnel-table-row">
              {/* Step Index & Name */}
              <div className="col-step-name">
                <span className="funnel-step-index">{idx + 1}</span>
                <span className="funnel-step-label" title={step.label}>
                  {step.label}
                </span>
              </div>

              {/* Progress Bar & Reached Stats */}
              <div className="col-progress">
                <div className="funnel-inline-track">
                  <div
                    className="funnel-inline-fill"
                    style={{ width: `${Math.max(2, reachedPct)}%` }}
                  />
                </div>
                <span className="funnel-reached-text">
                  <strong>{reached.toLocaleString()}</strong> ({reachedPct.toFixed(1)}%)
                </span>
              </div>

              {/* Drop-off Badge */}
              <div className="col-dropoff">
                {dropped > 0 ? (
                  <span className="funnel-pill pill-dropped" title={`${dropped.toLocaleString()} users dropped off at this step`}>
                    -{dropped.toLocaleString()} ({droppedPct}%)
                  </span>
                ) : (
                  <span className="funnel-pill pill-zero" title="No drop-offs recorded at this step">
                    0 dropped
                  </span>
                )}
              </div>

              {/* Conversion to Next Step */}
              <div className="col-transition">
                {!isLastStep ? (
                  <span className="funnel-pill pill-conv" title={`${convRate} transitioned to Step ${idx + 2}`}>
                    <FaArrowRight className="transition-icon" />
                    <span>{convRate} to Step {idx + 2}</span>
                  </span>
                ) : (
                  <span className="funnel-pill pill-terminal" title="Final step of the flow">
                    <FaCheckCircle className="terminal-check-icon" />
                    <span>Final Step</span>
                  </span>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Terminal Completion Strip */}
      <div className="funnel-terminal-strip">
        <div className="terminal-strip-content">
          <FaCheckCircle className="terminal-strip-icon" />
          <span>
            <strong>Flow Completed:</strong> {totalCompleted.toLocaleString()} users completed all {steps.length} steps ({overallRate} overall)
          </span>
        </div>
      </div>
    </div>
  );
}

StepFunnelView.propTypes = {
  nodes: PropTypes.array,
  summary: PropTypes.object
};

/**
 * FlowSankeyCard renders an individual flow's progression journey,
 * complete with KPI summary badges, interactive Sankey diagram, and Step Funnel view.
 */
export default function FlowSankeyCard({
  organizationId,
  siteCollectionId,
  flowId,
  flowName = '',
  fromUtc,
  toUtc,
  chartHeight: initialHeight = null,
  onRemove,
  onEmpty
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [viewMode, setViewMode] = useState('flow'); // 'flow' | 'funnel'
  const [selectedHeight, setSelectedHeight] = useState(initialHeight || 560);

  const fetchSankeyData = useCallback(async () => {
    if (!organizationId || !siteCollectionId || !flowId) {
      setData(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await getFlowSankey({
        organizationId,
        siteCollectionId,
        flowId,
        flowName,
        fromUtc,
        toUtc
      });
      setData(response);
      const isLaunchedEmpty = Number(response?.summary?.totalLaunched || 0) === 0;
      const isNodesEmpty = !Array.isArray(response?.nodes) || response.nodes.length === 0;
      if (isLaunchedEmpty && isNodesEmpty && onEmpty) {
        onEmpty(flowId);
      }
    } catch (err) {
      const msg = normalizeAnalyticsError(err, 'Failed to load flow journey analytics');
      setError(msg);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [organizationId, siteCollectionId, flowId, flowName, fromUtc, toUtc, onEmpty]);

  useEffect(() => {
    fetchSankeyData();
  }, [fetchSankeyData]);

  const displayName = data?.flowName || flowName || `Flow ${flowId}`;
  const summary = data?.summary || {
    totalLaunched: 0,
    totalCompleted: 0,
    totalExited: 0,
    overallCompletionRate: 0
  };

  const launched = Number(summary.totalLaunched || 0);
  const completed = Number(summary.totalCompleted || 0);
  const exited = Number(summary.totalExited || 0);
  const completionRate = summary.overallCompletionRate !== undefined && summary.overallCompletionRate !== null
    ? Number(summary.overallCompletionRate)
    : (launched > 0 ? Number(((completed / launched) * 100).toFixed(1)) : 0);

  return (
    <div className="flow-sankey-card" data-testid={`flow-sankey-card-${flowId}`}>
      {/* Header Row */}
      <div className="sankey-card-header">
        <div className="sankey-card-title-group">
          <div className="sankey-card-icon-wrap">
            <FaProjectDiagram className="sankey-card-icon" />
          </div>
          <div>
            <h3 className="sankey-card-title" title={displayName}>
              {displayName}
            </h3>
            <p className="sankey-card-subtitle">Step-by-step progression, transition volumes, and drop-off rates</p>
          </div>
        </div>

        {/* Right side: KPI badges and optional remove button */}
        <div className="sankey-card-header-actions">
          <div className="sankey-kpi-badges">
            <div className="sankey-kpi-badge badge-launched" title="Total users who launched this flow">
              <span className="kpi-badge-label">
                <FaRocket className="kpi-icon" /> Launched
              </span>
              <span className="kpi-badge-value">{launched.toLocaleString()}</span>
            </div>

            <div className="sankey-kpi-badge badge-completed" title="Users who completed the entire flow">
              <span className="kpi-badge-label">
                <FaCheckCircle className="kpi-icon" /> Completed
              </span>
              <span className="kpi-badge-value">{completed.toLocaleString()}</span>
            </div>

            <div className="sankey-kpi-badge badge-exited" title="Users who exited before finishing">
              <span className="kpi-badge-label">
                <FaTimesCircle className="kpi-icon" /> Dropped Off
              </span>
              <span className="kpi-badge-value">{exited.toLocaleString()}</span>
            </div>

            <div className="sankey-kpi-badge badge-rate" title="Overall journey completion rate">
              <span className="kpi-badge-label">
                <FaChartLine className="kpi-icon" /> Rate
              </span>
              <span className="kpi-badge-value">{completionRate}%</span>
            </div>
          </div>

          {onRemove && (
            <button
              type="button"
              className="sankey-card-remove-btn"
              onClick={() => onRemove(flowId)}
              title="Remove this flow from view"
              aria-label={`Remove flow ${displayName}`}
              data-testid={`remove-flow-btn-${flowId}`}
            >
              <FaTimes />
            </button>
          )}
        </div>
      </div>

      {/* Toolbar row: View Mode Toggle and Height Controls */}
      <div className="sankey-card-toolbar">
        <div className="card-view-mode-toggles">
          <button
            type="button"
            className={`card-view-btn ${viewMode === 'flow' ? 'active' : ''}`}
            onClick={() => setViewMode('flow')}
            title="Interactive Journey Sankey Diagram"
          >
            <FaStream className="view-btn-icon" /> Journey Flow
          </button>
          <button
            type="button"
            className={`card-view-btn ${viewMode === 'funnel' ? 'active' : ''}`}
            onClick={() => setViewMode('funnel')}
            title="Step-by-step Funnel Breakdown"
          >
            <FaFilter className="view-btn-icon" /> Step Funnel
          </button>
        </div>

        {viewMode === 'flow' && (
          <div className="card-height-controls">
            <span className="height-control-label">Box Height:</span>
            {[440, 560, 680].map((h) => (
              <button
                key={h}
                type="button"
                className={`height-pill-btn ${selectedHeight === h ? 'active' : ''}`}
                onClick={() => setSelectedHeight(h)}
              >
                {h}px
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Main Chart Body */}
      <div className="sankey-card-body">
        {loading ? (
          <div className="sankey-card-loading" data-testid="flow-sankey-loading">
            <div className="loading-spinner-ring" />
            <p>Analyzing flow journey progression...</p>
          </div>
        ) : error ? (
          <div className="sankey-card-error" data-testid="flow-sankey-error">
            <FaExclamationTriangle className="error-icon" />
            <div className="error-text-wrap">
              <p className="error-msg">{error}</p>
              <button
                type="button"
                className="sankey-retry-btn"
                onClick={fetchSankeyData}
              >
                <FaSyncAlt /> Retry Telemetry
              </button>
            </div>
          </div>
        ) : viewMode === 'funnel' ? (
          <StepFunnelView nodes={data?.nodes} summary={summary} />
        ) : (
          <FlowSankeyChart
            data={data}
            height={selectedHeight}
            enhancedVisibility={true}
          />
        )}
      </div>
    </div>
  );
}

FlowSankeyCard.propTypes = {
  organizationId: PropTypes.string.isRequired,
  siteCollectionId: PropTypes.string.isRequired,
  flowId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  flowName: PropTypes.string,
  fromUtc: PropTypes.string,
  toUtc: PropTypes.string,
  chartHeight: PropTypes.number,
  onRemove: PropTypes.func,
  onEmpty: PropTypes.func
};
